import React, { useState } from "react";
import { AlertTriangle, Check, Copy, Home, Loader2, RotateCw, Wifi } from "lucide-react";
import { Button } from "src/components/ui/button";
import { cn } from "src/lib/utils";
import { getErrorMessage } from "src/lib/errorReporter";
import { LoadingPill } from "./LoadingIndicator";

const SHOW_STACK = process.env.NODE_ENV === "development";

const TEXT = {
  title: "Something went wrong",
  sending: "Reporting the error…",
  reported:
    "An unexpected error occurred. Our team has been notified and will fix it as soon as possible.",
  failed:
    "We couldn't report this error automatically. Please contact support and mention the reference below.",
  failedNoReference:
    "We couldn't report this error automatically. Please contact support and describe what you were doing.",
  notFoundTitle: "Page not found",
  notFound: "The page you're looking for doesn't exist or has been moved.",
  connectingTitle: "Connecting to the server",
  connecting:
    "We can't reach the server right now. We'll keep trying automatically, this usually takes a few seconds.",
  restoredTitle: "Connection restored",
  restored: "The server is available again. Reload the page to load the latest data.",
};

/* Maps a reportError() result to the status used by ErrorContent */
export function reportStatusFrom(result) {
  return result?.reported ? "reported" : "failed";
}

function bodyText({ notFound, reportStatus, reference }) {
  if (notFound) return TEXT.notFound;
  if (reportStatus === "reported") return TEXT.reported;
  if (reportStatus === "failed") return reference ? TEXT.failed : TEXT.failedNoReference;
  return TEXT.sending;
}

function Reference({ value }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available: the value is still selectable
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
      <span>Reference:</span>
      <code className="select-all break-all rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Copy reference"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
    </div>
  );
}

/*
 * Presentational error content shared by the full-screen fallback (ErrorScreen) and the
 * global error modal. `titleAs` / `descriptionAs` let the modal plug in the dialog's
 * accessible Title/Description components.
 */
export function ErrorContent({
  error,
  reportStatus = "sending",
  reference,
  notFound = false,
  extraCount = 0,
  onReload,
  onHome,
  onDismiss,
  primaryButtonRef,
  titleAs: Title = "h2",
  descriptionAs: Description = "p",
}) {
  const message = error ? getErrorMessage(error) : null;
  const stack = SHOW_STACK && typeof error?.stack === "string" ? error.stack : null;

  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-col items-center gap-1.5">
          <Title className="m-0 text-lg font-semibold leading-tight text-foreground">
            {notFound ? TEXT.notFoundTitle : TEXT.title}
          </Title>
          <Description className="m-0 max-w-sm text-sm text-muted-foreground" aria-live="polite">
            {bodyText({ notFound, reportStatus, reference })}
          </Description>
        </div>
      </div>

      {!notFound && reference && <Reference value={reference} />}

      {extraCount > 0 && (
        <p className="m-0 text-xs text-muted-foreground">
          {extraCount === 1
            ? "1 more error occurred while this message was open."
            : `${extraCount} more errors occurred while this message was open.`}
        </p>
      )}

      {!notFound && message && (
        <details className="w-full rounded-md border bg-muted/40 px-3 py-2 text-left text-xs">
          <summary className="cursor-pointer select-none font-medium text-muted-foreground">
            Technical details
          </summary>
          <p className="mb-0 mt-2 break-words font-mono text-foreground">{message}</p>
          {stack && (
            <pre className="mb-0 mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
              {stack}
            </pre>
          )}
        </details>
      )}

      <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:justify-center">
        {onHome && (
          <Button
            type="button"
            variant={notFound ? "default" : "outline"}
            onClick={onHome}
            ref={notFound ? primaryButtonRef : undefined}
          >
            <Home className="mr-2 h-4 w-4" aria-hidden="true" />
            Go to home
          </Button>
        )}
        {!notFound && onReload && (
          <Button
            type="button"
            variant={onDismiss ? "outline" : "default"}
            onClick={onReload}
            ref={onDismiss ? undefined : primaryButtonRef}
          >
            <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Reload page
          </Button>
        )}
        {onDismiss && (
          <Button type="button" onClick={onDismiss} ref={primaryButtonRef}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
}

/*
 * Shown by the global error modal instead of ErrorContent when the backend cannot be reached:
 * a loading state while it reconnects automatically, then "Connection restored".
 */
export function ServerConnectionContent({
  restored = false,
  attempt = 0,
  onRetryNow,
  onReload,
  onDismiss,
  primaryButtonRef,
  titleAs: Title = "h2",
  descriptionAs: Description = "p",
}) {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="flex flex-col items-center gap-3">
        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
            restored ? "bg-emerald-100 text-emerald-700" : "bg-primary/10 text-primary"
          )}
        >
          {restored ? (
            <Wifi className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
          )}
        </div>
        <div className="flex min-w-0 flex-col items-center gap-1.5">
          <Title className="m-0 text-lg font-semibold leading-tight text-foreground">
            {restored ? TEXT.restoredTitle : TEXT.connectingTitle}
          </Title>
          <Description className="m-0 max-w-sm text-sm text-muted-foreground" aria-live="polite">
            {restored ? TEXT.restored : TEXT.connecting}
          </Description>
        </div>
      </div>

      {!restored && (
        <div className="flex flex-col items-center gap-2" role="status" aria-live="polite">
          <LoadingPill label={attempt > 0 ? `Reconnecting… (attempt ${attempt + 1})` : "Reconnecting…"} />
        </div>
      )}

      <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:justify-center">
        {onDismiss && (
          <Button type="button" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
        {!restored && onRetryNow && (
          <Button type="button" variant="outline" onClick={onRetryNow} ref={primaryButtonRef}>
            Retry now
          </Button>
        )}
        {onReload && (
          <Button
            type="button"
            variant={restored ? "default" : "outline"}
            onClick={onReload}
            ref={restored ? primaryButtonRef : undefined}
          >
            <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Reload page
          </Button>
        )}
      </div>
    </div>
  );
}

/* Full-screen (or full-area) fallback used for render crashes */
export default function ErrorScreen({ fullHeight = true, className, ...props }) {
  const primaryButtonRef = React.useRef(null);

  React.useEffect(() => {
    primaryButtonRef.current?.focus();
  }, []);

  return (
    <div
      role="alert"
      className={cn(
        "flex w-full items-center justify-center bg-background px-4 py-8",
        fullHeight ? "min-h-screen" : "min-h-[60vh]",
        className
      )}
    >
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
        <ErrorContent {...props} primaryButtonRef={primaryButtonRef} />
      </div>
    </div>
  );
}
